import { useState, useEffect, useCallback } from 'react';
import { SuspiciousActivitiesTab } from '@/components/admin/security/SuspiciousActivitiesTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Search, 
  Filter, 
  MoreHorizontal, 
  Monitor,
  Smartphone,
  Tablet,
  Eye,
  Shield,
  AlertTriangle,
  Clock,
  MapPin,
  User,
  RefreshCw,
  Users,
  Activity,
  ShieldAlert,
  LogOut,
  UserX,
  Calendar,
  Globe,
  Trash2
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/useAuth';

interface UserDevice {
  id: string;
  user_id: string;
  device_name: string;
  device_type: string;
  browser_info?: string;
  ip_address?: string;
  is_active: boolean;
  last_seen: string;
  created_at: string;
  location_info?: any;
  device_fingerprint?: string;
}

interface SessionWithUser extends UserDevice {
  user_email?: string;
  user_name?: string;
  user_role?: string;
  ip_address?: string;
  profiles?: {
    email: string;
    user_id: string;
    first_name?: string;
    last_name?: string;
    role?: string;
  };
}

interface SecurityStats {
  activeSessionsCount: number;
  totalDevicesCount: number;
  securityEventsCount: number;
  avgSessionDuration: string;
  recentLogins: number;
  failedLoginAttempts: number;
}

const SessionManagement = () => {
  const [sessions, setSessions] = useState<SessionWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deviceFilter, setDeviceFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [selectedSession, setSelectedSession] = useState<SessionWithUser | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
  const [stats, setStats] = useState<SecurityStats>({
    activeSessionsCount: 0,
    totalDevicesCount: 0,
    securityEventsCount: 0,
    avgSessionDuration: '0m',
    recentLogins: 0,
    failedLoginAttempts: 0
  });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const { toast } = useToast();
  const { userRole, loading: authLoading } = useAuth();

  const sessionsPerPage = 10;

  const fetchSessions = useCallback(async (isRefresh = false) => {
    // Only fetch if user is admin
    if (userRole !== 'admin') {
      setLoading(false);
      return;
    }

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      // Fetch all user devices with enhanced data
      let devicesQuery = supabase
        .from('user_devices')
        .select('*, location_info, device_fingerprint', { count: 'exact' });

      // Apply filters
      if (searchTerm) {
        devicesQuery = devicesQuery.or(
          `device_name.ilike.%${searchTerm}%,browser_info.ilike.%${searchTerm}%`
        );
      }

      if (deviceFilter !== 'all') {
        devicesQuery = devicesQuery.eq('device_type', deviceFilter);
      }

      if (statusFilter !== 'all') {
        devicesQuery = devicesQuery.eq('is_active', statusFilter === 'active');
      }

      const { data: allDevices, error: devicesError, count } = await devicesQuery
        .order('last_seen', { ascending: false });

      if (devicesError) throw devicesError;

      // Get unique user IDs
      const userIds = [...new Set((allDevices || []).map(device => device.user_id))];
      
      // Get profiles and user roles for these users  
      const [profilesResult, userRolesResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('user_id, email, first_name, last_name, role')
          .in('user_id', userIds),
        supabase
          .from('user_roles')
          .select('user_id, role')
          .in('user_id', userIds)
      ]);

      if (profilesResult.error) throw profilesResult.error;

      // Create profiles map for quick lookup
      const profilesMap = new Map();
      const userRolesMap = new Map();
      
      (profilesResult.data || []).forEach(profile => {
        profilesMap.set(profile.user_id, profile);
      });
      
      (userRolesResult.data || []).forEach(userRole => {
        userRolesMap.set(userRole.user_id, userRole.role);
      });

      // Apply role filter and transform data
      let filteredDevices = (allDevices || []).filter(device => {
        const userRole = userRolesMap.get(device.user_id) || 'user';
        return roleFilter === 'all' || userRole === roleFilter;
      });

      // Apply search filter across user data
      if (searchTerm) {
        filteredDevices = filteredDevices.filter(device => {
          const profile = profilesMap.get(device.user_id);
          const deviceNameMatch = device.device_name?.toLowerCase().includes(searchTerm.toLowerCase());
          const emailMatch = profile?.email?.toLowerCase().includes(searchTerm.toLowerCase());
          const browserMatch = device.browser_info?.toLowerCase().includes(searchTerm.toLowerCase());
          const nameMatch = `${profile?.first_name || ''} ${profile?.last_name || ''}`.toLowerCase().includes(searchTerm.toLowerCase());
          return deviceNameMatch || emailMatch || browserMatch || nameMatch;
        });
      }

      // Pagination
      const from = (currentPage - 1) * sessionsPerPage;
      const to = from + sessionsPerPage;
      const paginatedDevices = filteredDevices.slice(from, to);

      // Transform data for display with enhanced information
      const transformedData = paginatedDevices.map(device => {
        const profile = profilesMap.get(device.user_id);
        const userRole = userRolesMap.get(device.user_id) || 'user';
        return {
          ...device,
          user_email: profile?.email || 'Unknown',
          user_name: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Unknown User',
          user_role: userRole,
          profiles: profile,
          ip_address: device.ip_address as string || 'Unknown'
        };
      });

      // Fetch and calculate statistics
      const [securityEventsResult, recentLoginsResult] = await Promise.all([
        supabase
          .from('suspicious_activities')
          .select('id, severity, status', { count: 'exact' })
          .eq('status', 'pending'),
        supabase
          .from('user_devices')
          .select('created_at', { count: 'exact' })
          .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
      ]);

      // Calculate enhanced statistics
      const activeDevices = (allDevices || []).filter(d => d.is_active);
      const activeSessions = activeDevices.filter(d => {
        const lastSeen = new Date(d.last_seen);
        const minutesDiff = (Date.now() - lastSeen.getTime()) / (1000 * 60);
        return minutesDiff < 5;
      });

      // Calculate average session duration
      const sessionDurations = activeDevices.map(device => {
        const created = new Date(device.created_at);
        const lastSeen = new Date(device.last_seen);
        return Math.abs(lastSeen.getTime() - created.getTime()) / (1000 * 60); // in minutes
      });
      
      const avgDuration = sessionDurations.length > 0 
        ? Math.round(sessionDurations.reduce((a, b) => a + b, 0) / sessionDurations.length)
        : 0;

      const newStats: SecurityStats = {
        activeSessionsCount: activeSessions.length,
        totalDevicesCount: (allDevices || []).length,
        securityEventsCount: securityEventsResult.count || 0,
        avgSessionDuration: `${avgDuration}m`,
        recentLogins: recentLoginsResult.count || 0,
        failedLoginAttempts: 0 // This would need additional tracking
      };

      setSessions(transformedData);
      setTotalSessions(filteredDevices.length);
      setStats(newStats);
      
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sessions',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentPage, searchTerm, deviceFilter, statusFilter, roleFilter, toast, userRole]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh || authLoading || userRole !== 'admin') return;
    
    const interval = setInterval(() => {
      fetchSessions(true);
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, [autoRefresh, fetchSessions, authLoading, userRole]);

  useEffect(() => {
    if (!authLoading) {
      fetchSessions();
    }
  }, [fetchSessions, authLoading]);

  // Bulk actions
  const handleSelectSession = (sessionId: string, checked: boolean) => {
    if (checked) {
      setSelectedSessions(prev => [...prev, sessionId]);
    } else {
      setSelectedSessions(prev => prev.filter(id => id !== sessionId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSessions(sessions.map(s => s.id));
    } else {
      setSelectedSessions([]);
    }
  };

  const terminateSelectedSessions = async () => {
    try {
      const { error } = await supabase
        .from('user_devices')
        .update({ is_active: false })
        .in('id', selectedSessions);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `${selectedSessions.length} sessions terminated successfully`,
      });

      setSelectedSessions([]);
      setBulkDialogOpen(false);
      fetchSessions();
    } catch (error) {
      console.error('Error terminating sessions:', error);
      toast({
        title: 'Error',
        description: 'Failed to terminate sessions',
        variant: 'destructive',
      });
    }
  };

  const terminateUserSessions = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('user_devices')
        .update({ is_active: false })
        .eq('user_id', userId)
        .eq('is_active', true);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'All user sessions terminated successfully',
      });

      fetchSessions();
    } catch (error) {
      console.error('Error terminating user sessions:', error);
      toast({
        title: 'Error',
        description: 'Failed to terminate user sessions',
        variant: 'destructive',
      });
    }
  };

  const terminateSession = async (sessionId: string) => {
    try {
      const { error } = await supabase
        .from('user_devices')
        .update({ is_active: false })
        .eq('id', sessionId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Session terminated successfully',
      });

      fetchSessions();
    } catch (error) {
      console.error('Error terminating session:', error);
      toast({
        title: 'Error',
        description: 'Failed to terminate session',
        variant: 'destructive',
      });
    }
  };

  const viewSessionDetails = (session: SessionWithUser) => {
    setSelectedSession(session);
    setDetailsDialogOpen(true);
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType.toLowerCase()) {
      case 'mobile': return Smartphone;
      case 'tablet': return Tablet;
      case 'desktop':
      default: return Monitor;
    }
  };

  const getStatusColor = (isActive: boolean, lastSeen: string) => {
    if (!isActive) return 'secondary';
    
    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const minutesDiff = (now.getTime() - lastSeenDate.getTime()) / (1000 * 60);
    
    if (minutesDiff < 5) return 'default'; // Active (green)
    if (minutesDiff < 30) return 'outline'; // Recently active (yellow)
    return 'secondary'; // Inactive (gray)
  };

  const getStatusText = (isActive: boolean, lastSeen: string) => {
    if (!isActive) return 'Terminated';
    
    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const minutesDiff = (now.getTime() - lastSeenDate.getTime()) / (1000 * 60);
    
    if (minutesDiff < 5) return 'Active';
    if (minutesDiff < 30) return 'Recent';
    return 'Idle';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(totalSessions / sessionsPerPage);

  // Show loading state while checking permissions
  if (authLoading) {
    return (
      <DashboardLayout isAdmin={true}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-lg">Loading...</div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Show access denied if not admin
  if (userRole !== 'admin') {
    return (
      <DashboardLayout isAdmin={false}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-destructive mb-4">Access Denied</h1>
            <p className="text-muted-foreground">
              You do not have permission to access the session management page.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout isAdmin={true}>
      <div className="space-y-6">
        {/* Enhanced Header with Controls */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Session Management</h1>
            <p className="text-muted-foreground">
              Monitor active sessions, devices, and security events
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchSessions(true)}
              disabled={refreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              <Activity className="h-4 w-4 mr-2" />
              Auto-refresh
            </Button>
          </div>
        </div>

        <Tabs defaultValue="sessions" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="sessions">Active Sessions</TabsTrigger>
            <TabsTrigger value="security">Security Alerts</TabsTrigger>
          </TabsList>

          <TabsContent value="sessions" className="space-y-6">

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Sessions</CardTitle>
              <Shield className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activeSessionsCount}</div>
              <p className="text-xs text-muted-foreground">Real-time active sessions</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Devices</CardTitle>
              <Monitor className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalDevicesCount}</div>
              <p className="text-xs text-muted-foreground">All registered devices</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Security Events</CardTitle>
              <AlertTriangle className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.securityEventsCount}</div>
              <p className="text-xs text-muted-foreground">Pending alerts</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Session Time</CardTitle>
              <Clock className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.avgSessionDuration}</div>
              <p className="text-xs text-muted-foreground">Average duration</p>
            </CardContent>
          </Card>
        </div>

        {/* Sessions Table */}
        <Card>
          <CardHeader>
            <CardTitle>Device Sessions ({totalSessions})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by device, user, email, or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={deviceFilter} onValueChange={setDeviceFilter}>
                <SelectTrigger className="w-[150px]">
                  <Monitor className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Device" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Devices</SelectItem>
                  <SelectItem value="desktop">Desktop</SelectItem>
                  <SelectItem value="mobile">Mobile</SelectItem>
                  <SelectItem value="tablet">Tablet</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px]">
                  <Shield className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-[130px]">
                  <Users className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="moderator">Moderator</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bulk Actions Bar */}
            {selectedSessions.length > 0 && (
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">
                    {selectedSessions.length} session{selectedSessions.length !== 1 ? 's' : ''} selected
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedSessions([])}
                  >
                    Clear Selection
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setBulkDialogOpen(true)}
                    disabled={selectedSessions.length === 0}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Terminate Selected
                  </Button>
                </div>
              </div>
            )}

            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={
                          sessions.length > 0 && 
                          sessions.every(session => selectedSessions.includes(session.id))
                        }
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all sessions"
                      />
                    </TableHead>
                    <TableHead>User & Device</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Device Info</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Last Seen</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        Loading sessions...
                      </TableCell>
                    </TableRow>
                  ) : sessions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No sessions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sessions.map((session) => {
                      const DeviceIcon = getDeviceIcon(session.device_type);
                      return (
                        <TableRow key={session.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedSessions.includes(session.id)}
                              onCheckedChange={(checked) => 
                                handleSelectSession(session.id, checked as boolean)
                              }
                              aria-label={`Select session for ${session.user_email}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <DeviceIcon className="h-5 w-5 text-primary" />
                               <div>
                                 <div className="font-medium">{session.device_name}</div>
                               <div className="text-sm text-muted-foreground flex items-center gap-1">
                                 <User className="h-3 w-3" />
                                 {session.user_name} ({session.user_email})
                               </div>
                               </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={session.user_role === 'admin' ? 'destructive' : session.user_role === 'moderator' ? 'default' : 'secondary'}>
                              {session.user_role || 'user'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={getStatusColor(session.is_active, session.last_seen)}>
                              {getStatusText(session.is_active, session.last_seen)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div className="font-medium capitalize">{session.device_type}</div>
                              {session.browser_info && (
                                <div className="text-muted-foreground truncate max-w-[150px]" title={session.browser_info}>
                                  {session.browser_info}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              {session.ip_address || 'Unknown'}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{formatDate(session.last_seen)}</TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => viewSessionDetails(session)}>
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                {session.is_active && (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => terminateSession(session.id)}
                                      className="text-destructive"
                                    >
                                      <LogOut className="h-4 w-4 mr-2" />
                                      Terminate Session
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => terminateUserSessions(session.user_id)}
                                      className="text-destructive"
                                    >
                                      <UserX className="h-4 w-4 mr-2" />
                                      Terminate All User Sessions
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-4">
                <div className="text-sm text-muted-foreground">
                  Showing {(currentPage - 1) * sessionsPerPage + 1} to{' '}
                  {Math.min(currentPage * sessionsPerPage, totalSessions)} of {totalSessions} sessions
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Session Details Dialog */}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Session Details</DialogTitle>
              <DialogDescription>
                Detailed information about the selected device session
              </DialogDescription>
            </DialogHeader>
            {selectedSession && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Device Name</label>
                    <p className="text-sm">{selectedSession.device_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Device Type</label>
                    <p className="text-sm capitalize">{selectedSession.device_type}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">User ID</label>
                    <p className="text-sm font-mono">{selectedSession.user_id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    <p className="text-sm">
                      <Badge variant={getStatusColor(selectedSession.is_active, selectedSession.last_seen)}>
                        {getStatusText(selectedSession.is_active, selectedSession.last_seen)}
                      </Badge>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Browser Info</label>
                    <p className="text-sm">{selectedSession.browser_info || 'Not available'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">IP Address</label>
                    <p className="text-sm">{selectedSession.ip_address || 'Unknown'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Created At</label>
                    <p className="text-sm">{formatDate(selectedSession.created_at)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Last Seen</label>
                    <p className="text-sm">{formatDate(selectedSession.last_seen)}</p>
                  </div>
                </div>
                {selectedSession.is_active && (
                  <div className="pt-4 border-t">
                    <Button
                      onClick={() => {
                        terminateSession(selectedSession.id);
                        setDetailsDialogOpen(false);
                      }}
                      variant="destructive"
                      className="w-full"
                    >
                      <Shield className="h-4 w-4 mr-2" />
                      Terminate Session
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Bulk Action Confirmation Dialog */}
        <AlertDialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Bulk Session Termination</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to terminate {selectedSessions.length} selected session{selectedSessions.length !== 1 ? 's' : ''}? 
                This action cannot be undone and will immediately log out the affected users.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={terminateSelectedSessions}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Terminate Sessions
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
          </TabsContent>

          <TabsContent value="security">
            <SuspiciousActivitiesTab />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default SessionManagement;